import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Theme,
  Typography,
  Chip,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import EventIcon from "@material-ui/icons/Event";
import AssignmentIcon from "@material-ui/icons/Assignment";
import GradeIcon from "@material-ui/icons/Grade";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

import GroupIcon from "@material-ui/icons/Group";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import { useAppDispatch, useAppSelector } from "app/hooks";
import InputField from "components/FormFields/InputField";
import { RadioGroupField } from "components/FormFields/RadioGroupFiled";
import {
  fetchDataOptions,
  selectListDepartment,
  selectListClass,
} from "features/Auth/options/optionsSlice";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import managerTestApi from "api/managerTest";

import { ListExam, Question } from "models/managerTest";
import * as yup from "yup";

import CreateQuestion from "./components/CreateQuestion";
import ManageExamStudentsModal from "components/ManageExamStudentsModal";

export default function EditExam() {
  const useStyles = makeStyles((theme: Theme) => ({
    pageWrapper: {
      maxWidth: 1200,
      margin: "0 auto",
      width: "100%",
      paddingBottom: 40,
      minHeight: "calc(100vh - 120px)",
      display: "flex",
      flexDirection: "column",
    },
    headerBanner: {
      background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
      borderRadius: 24,
      padding: "28px 32px",
      color: "#FFFFFF",
      marginBottom: 24,
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.15)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 16,
      [theme.breakpoints.down("sm")]: {
        padding: "20px 18px",
        borderRadius: 16,
      },
      "&::after": {
        content: '""',
        position: "absolute",
        top: "-40%",
        right: "-10%",
        width: 280,
        height: 280,
        background: "radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, rgba(99, 102, 241, 0) 70%)",
        pointerEvents: "none",
      },
    },
    stepperCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 16,
      padding: "16px 24px",
      marginBottom: 24,
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
      [theme.breakpoints.down("sm")]: {
        padding: "12px 8px",
      },
    },
    formCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      padding: "28px 32px",
      marginBottom: 24,
      border: "1px solid #E2E8F0",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
      [theme.breakpoints.down("sm")]: {
        padding: "20px 16px",
      },
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 20,
      paddingBottom: 12,
      borderBottom: "1px solid #F1F5F9",
    },
    sectionTitle: {
      fontSize: "1.1rem",
      fontWeight: 700,
      color: "#0F172A",
    },
    sectionIcon: {
      color: "#4F46E5",
      fontSize: 22,
    },
    fieldLabel: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#334155",
      marginBottom: 8,
      display: "block",
    },
    timeHelperBadge: {
      backgroundColor: "#EEF2FF",
      color: "#4F46E5",
      fontSize: "0.75rem",
      fontWeight: 600,
      borderRadius: 6,
      padding: "4px 10px",
      marginTop: 6,
      display: "inline-block",
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
      color: "#FFFFFF",
      borderRadius: 12,
      padding: "10px 24px",
      fontWeight: 700,
      fontSize: "0.9rem",
      textTransform: "none",
      boxShadow: "0 4px 14px rgba(37, 99, 235, 0.3)",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        background: "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)",
        boxShadow: "0 6px 18px rgba(37, 99, 235, 0.4)",
      },
    },
    btnSecondary: {
      backgroundColor: "#F1F5F9",
      color: "#475569",
      borderRadius: 12,
      padding: "10px 20px",
      fontWeight: 600,
      fontSize: "0.9rem",
      textTransform: "none",
      border: "1px solid #CBD5E1",
      marginRight: 12,
      "&:hover": {
        backgroundColor: "#E2E8F0",
        color: "#0F172A",
      },
    },
    backButton: {
      backgroundColor: "rgba(255, 255, 255, 0.15)",
      color: "#FFFFFF",
      borderRadius: 12,
      padding: "8px 12px",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.25)",
      },
    },
    stickyFooter: {
      position: "sticky",
      bottom: 16,
      zIndex: 100,
      backgroundColor: "rgba(255, 255, 255, 0.92)",
      backdropFilter: "blur(12px)",
      borderRadius: 16,
      padding: "14px 24px",
      border: "1px solid #E2E8F0",
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.1)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "auto",
    },
  }));

  const classes = useStyles();
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [detailExam, setDetailExam] = useState<ListExam>();
  const [activeStep, setActiveStep] = useState(0);
  const [valueForm, setValueForm] = useState<Question>();
  const [createQuestion, setCreateQuestion] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // Parent sticky overflow fix from AGENTS.md
  useEffect(() => {
    const targetParents: HTMLElement[] = [];
    const originalOverflows: string[] = [];

    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent && parent.tagName !== "MAIN") {
        const computedStyle = window.getComputedStyle(parent);
        if (
          computedStyle.overflow === "hidden" ||
          computedStyle.overflowX === "hidden" ||
          computedStyle.overflowY === "hidden"
        ) {
          targetParents.push(parent);
          originalOverflows.push(parent.style.overflow);
          parent.style.overflow = "visible";
        }
        parent = parent.parentElement;
      }
    }

    return () => {
      targetParents.forEach((parent, index) => {
        if (parent) {
          parent.style.overflow = originalOverflows[index];
        }
      });
    };
  }, [detailExam]);

  useEffect(() => {
    (async () => {
      if (params.id) {
        try {
          const res: ListExam = await managerTestApi.getDetailExam(Number(params.id));
          setDetailExam(res.exam);
        } catch (error) {
          toast.error("Không thể tải thông tin đề thi");
        }
      }
    })();
  }, [params.id]);

  const listDepartment = useAppSelector(selectListDepartment);
  const classList = useAppSelector(selectListClass);
  const [openStudentModal, setOpenStudentModal] = useState(false);


  useEffect(() => {
    dispatch(fetchDataOptions());
  }, [dispatch]);

  const schema = yup.object().shape({
    startDateTime: yup.string().required(`${t("managerTest.vuilongchonngaykiemtra")}`),
    publishAnswerDateTime: yup.string().required(`${t("managerTest.vuilongchonngayhienthidapan")}`),
    examTime: yup.string().required(`${t("managerTest.vuilongnhapthoigianthi")}`),
    category: yup.string().required("*Vui lòng chọn hình thức kiểm tra"),
    classId: yup.array().min(1, "*Vui lòng chọn lớp học"),
  });

  const parseClassIds = (exam: any): number[] => {
    if (!exam) return [];
    if (exam.classIdList) {
      if (Array.isArray(exam.classIdList)) {
        return exam.classIdList.map((x: any) => Number(x)).filter(Boolean);
      }
      if (typeof exam.classIdList === "string") {
        return exam.classIdList
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean)
          .map((s: string) => Number(s))
          .filter((n: number) => !isNaN(n));
      }
    }
    if (exam.gpClassList && Array.isArray(exam.gpClassList)) {
      return exam.gpClassList.map((c: any) => Number(c.id)).filter(Boolean);
    }
    if (exam.gpClasses && Array.isArray(exam.gpClasses)) {
      return exam.gpClasses.map((c: any) => Number(c.id)).filter(Boolean);
    }
    return [];
  };

  const parseUserIds = (exam: any): number[] => {
    if (!exam) return [];
    if (exam.userIdList) {
      if (Array.isArray(exam.userIdList)) {
        return exam.userIdList.map((x: any) => Number(x)).filter(Boolean);
      }
      if (typeof exam.userIdList === "string") {
        return exam.userIdList
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean)
          .map((s: string) => Number(s))
          .filter((n: number) => !isNaN(n));
      }
    }
    if (exam.users && Array.isArray(exam.users)) {
      return exam.users.map((u: any) => Number(u.id)).filter(Boolean);
    }
    if (exam.userList && Array.isArray(exam.userList)) {
      return exam.userList.map((u: any) => Number(u.id)).filter(Boolean);
    }
    return [];
  };

  const initialValues: any = useMemo(() => {
    const classIds = parseClassIds(detailExam);
    const userIds = parseUserIds(detailExam);
    const isAllClass =
      classIds.length > 0 && classList.length > 0 && classIds.length === classList.length;

    return {
      departmentId: Number(detailExam?.departmentIds) || "",
      type: "",
      startDateTime: detailExam?.startDate
        ? moment(detailExam.startDate).format("YYYY-MM-DDTHH:mm")
        : "",
      examTime: detailExam?.examTime || 0,
      totalPoint: detailExam?.totalPoint || 0,
      category: detailExam?.type === "TYPING" ? "essay" : "multipleChoice",
      publishAnswerDateTime: detailExam?.publishAnswerDate
        ? moment(detailExam.publishAnswerDate).format("YYYY-MM-DDTHH:mm")
        : "",
      allClass: isAllClass,
      classId: classIds,
      userId: userIds,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailExam]);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset(initialValues);
  }, [reset, initialValues]);

  const watchExamTime = watch("examTime");

  const formattedDurationText = useMemo(() => {
    const minutes = Number(watchExamTime) || 0;
    if (minutes <= 0) return null;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs === 0) return `${mins} phút`;
    return `${hrs} giờ ${mins > 0 ? `${mins} phút` : ""}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchExamTime]);

  const handleSubmitForm = (value: any) => {
    const classIdList = value.classId || [];
    const userIdList = value.userId || [];

    if (!classIdList || classIdList.length === 0) {
      toast.error("Chưa chọn lớp học hoặc danh sách học viên dự thi! Vui lòng chọn ở mục 1.");
      return;
    }

    const startIso = value.startDateTime;
    const publishIso = value.publishAnswerDateTime;

    if (startIso && moment(startIso).isBefore(moment(), "minute")) {
      toast.error(`${t("managerTest.vuilongchongiokiemtralonhonhoacbanggiohientai")}`);
      return;
    }

    const examMinutes = Number(value.examTime) || 0;
    const endMoment = moment(startIso).add(examMinutes, "minutes");

    setValueForm({
      ...value,
      title: detailExam?.examTitle,
      startDate: startIso,
      time: moment(startIso).format("HH:mm"),
      prizeId: Number(value.prizeId) || "",
      totalPoint: Number(value.totalPoint) || 0,
      type: value.category === "multipleChoice" ? "CHOICES" : "TYPING",
      classId: classIdList,
      classIdList: classIdList,
      userId: userIdList,
      userIdList: userIdList,
      departmentId: listDepartment.map((x) => x.id),
      endDate: endMoment.format("YYYY-MM-DDTHH:mm"),
      publishAnswerDate: publishIso,
    });
    setCreateQuestion(true);
    setActiveStep(1);
  };

  const steps = [
    "Cấu hình chung",
    "Ngân hàng câu hỏi",
    "Xem trước & Cập nhật",
  ];

  return (
    <div ref={containerRef} className={classes.pageWrapper}>
      {/* Header Banner */}
      <Box className={classes.headerBanner}>
        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <IconButton className={classes.backButton} onClick={() => navigate(-1)}>
            <ArrowBackIosIcon style={{ fontSize: 18, marginLeft: 6 }} />
          </IconButton>
          <Box>
            <Typography variant="h5" style={{ fontWeight: 800, color: "#FFFFFF" }}>
              {detailExam?.examTitle || "Chỉnh sửa đề thi"}
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 4 }}>
              <Chip
                label={`Mã đề: #${params.id}`}
                size="small"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.2)", color: "#FFFFFF", fontWeight: 600 }}
              />
              <Chip
                label={detailExam?.type === "TYPING" ? "Tự luận" : "Trắc nghiệm"}
                size="small"
                style={{ backgroundColor: "#38BDF8", color: "#0F172A", fontWeight: 700 }}
              />
            </Box>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <Chip
            icon={<GradeIcon style={{ color: "#F59E0B" }} />}
            label={`Tổng điểm: ${detailExam?.totalPoint || 0}đ`}
            style={{ backgroundColor: "rgba(255, 255, 255, 0.15)", color: "#FFFFFF", fontWeight: 700 }}
          />
        </Box>
      </Box>

      {/* Stepper Navigation */}
      <Paper className={classes.stepperCard} elevation={0}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>
                <Typography style={{ fontWeight: 700, fontSize: "0.875rem", color: "#0F172A" }}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Content Body */}
      {createQuestion ? (
        <CreateQuestion
          valueForm={valueForm}
          setValueForm={setValueForm}
          setCreateQuestion={(val) => {
            setCreateQuestion(val);
            if (!val) setActiveStep(0);
          }}
          detailExam={detailExam}
        />
      ) : (
        <form onSubmit={handleSubmit(handleSubmitForm)} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box className={classes.formCard}>
            {/* Section 1: Đối tượng áp dụng (Lớp học & Học viên) */}
            <Box className={classes.sectionHeader}>
              <GroupIcon className={classes.sectionIcon} />
              <Typography className={classes.sectionTitle}>
                1. Lớp học & Học viên áp dụng
              </Typography>
            </Box>

            <Box
              style={{
                backgroundColor: "#F8FAFC",
                borderRadius: 16,
                border: "1px solid #E2E8F0",
                padding: "18px 24px",
                marginBottom: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 16,
              }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                <Box
                  style={{
                    backgroundColor: "#0284C7",
                    color: "#FFFFFF",
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GroupIcon style={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography style={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A" }}>
                    Danh Sách Lớp & Học Viên Dự Thi
                  </Typography>
                  <Typography style={{ fontSize: "0.85rem", color: "#64748B", marginTop: 2 }}>
                    Đã chọn: <b style={{ color: "#0284C7" }}>{(watch("classId") || []).length} Lớp học</b> •{" "}
                    <b style={{ color: "#10B981" }}>
                      {(watch("userId") || []).length > 0
                        ? `${(watch("userId") || []).length} Học viên chọn riêng`
                        : "Tất cả học viên trong lớp"}
                    </b>
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={() => setOpenStudentModal(true)}
                startIcon={<GroupIcon />}
                style={{
                  backgroundColor: "#0284C7",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  borderRadius: 10,
                  padding: "8px 20px",
                  textTransform: "none",
                  boxShadow: "0 4px 12px rgba(2, 132, 199, 0.25)",
                }}
              >
                Chọn Lớp & Danh Sách Học Viên
              </Button>
            </Box>

            {(!watch("classId") || watch("classId").length === 0) && (
              <Box
                style={{
                  backgroundColor: "#FEF2F2",
                  borderRadius: 12,
                  border: "1px solid #FCA5A5",
                  padding: "12px 16px",
                  marginBottom: 24,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <ErrorOutlineIcon style={{ color: "#DC2626", fontSize: 20 }} />
                <Typography style={{ fontSize: "0.875rem", fontWeight: 600, color: "#DC2626" }}>
                  Chưa chọn lớp học hoặc học viên dự thi. Vui lòng bấm nút "Chọn Lớp & Danh Sách Học Viên" ở trên để chọn!
                </Typography>
              </Box>
            )}

            {/* Section 2: Thời gian & Lịch thi */}
            <Box className={classes.sectionHeader}>
              <EventIcon className={classes.sectionIcon} />
              <Typography className={classes.sectionTitle}>
                2. Lịch thi & Thời gian làm bài
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <span className={classes.fieldLabel}>
                  {t("managerTest.ngaygiokiemtra")} *
                </span>
                <InputField
                  control={control}
                  type="datetime-local"
                  label=""
                  name="startDateTime"
                  size="small"
                  borderRadius="12px"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <span className={classes.fieldLabel}>
                  {t("managerTest.thoigianhienthidapan")} *
                </span>
                <InputField
                  control={control}
                  type="datetime-local"
                  label=""
                  name="publishAnswerDateTime"
                  size="small"
                  borderRadius="12px"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <span className={classes.fieldLabel}>
                  {t("managerTest.thoigianlambai")} (phút) *
                </span>
                <InputField
                  control={control}
                  name="examTime"
                  label={t("managerTest.nhapthoigianthi")}
                />
                {formattedDurationText && (
                  <span className={classes.timeHelperBadge}>
                    ⏱️ Quy đổi: {formattedDurationText}
                  </span>
                )}
              </Grid>
            </Grid>

            {/* Section 3: Hình thức & Thang điểm */}
            <Box className={classes.sectionHeader} style={{ marginTop: 32 }}>
              <AssignmentIcon className={classes.sectionIcon} />
              <Typography className={classes.sectionTitle}>
                3. Hình thức kiểm tra & Thang điểm
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <span className={classes.fieldLabel}>
                  {t("managerTest.hinhthuckiemtra")}
                </span>
                <Box
                  style={{
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: 10,
                    padding: "4px 14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <RadioGroupField
                    name="category"
                    label=""
                    disabled
                    control={control}
                    options={[
                      { label: `${t("managerTest.tracnghiem")}`, value: "multipleChoice" },
                      { label: `${t("managerTest.tuluan")}`, value: "essay" },
                    ]}
                  />
                  <Chip
                    size="small"
                    label="🔒 Không thể sửa"
                    style={{ backgroundColor: "#E2E8F0", color: "#64748B", fontWeight: 600, fontSize: "0.75rem" }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <span className={classes.fieldLabel}>
                  {t("managerTest.tongdiem")} *
                </span>
                <InputField
                  control={control}
                  name="totalPoint"
                  label={t("managerTest.nhaptongdiem")}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Sticky Actions Footer */}
          <Box className={classes.stickyFooter}>
            <Button
              className={classes.btnSecondary}
              onClick={() => navigate("/admin/manager-test/list")}
            >
              Hủy bỏ
            </Button>
            <Button
              type="submit"
              className={classes.btnPrimary}
              endIcon={<ArrowForwardIcon />}
            >
              Tiếp theo
            </Button>
          </Box>
        </form>
      )}

      <ManageExamStudentsModal
        open={openStudentModal}
        onClose={() => setOpenStudentModal(false)}
        classList={classList}
        initialClassIds={watch("classId") || []}
        initialUserIds={watch("userId") || []}
        onSave={(newClassIds, newUserIds) => {
          setValue("classId", newClassIds);
          setValue("userId", newUserIds);
        }}
      />
    </div>
  );
}

